import React, { isValidElement, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import classNames from "classnames";
import { useParams } from "react-router-dom";
import { FieldArray } from "react-final-form-arrays";
import { useForm, useFormState } from "react-final-form";
import { getNewUniqueName, getNextMaxId } from "utils/data";
import { isDev } from "api/url-builder";
import PreviewGraph from "../PreviewGraph";
import { SimulationFormFields, VariantSourceType } from "../..";
import RadioInputGroup from "components/Controls/RadioInputGroup";
import ThreeDotsMenu from "components/ThreeDotsMenu";
import DefaultThreeDotsMenuItem from "components/ThreeDotsMenu/Components/DefaultThreeDotsMenuItem";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import ParameterSection from "./components/ParameterSection";
import AlternativeFlowSection from "./components/AlternativeFlowSection";
import RenameDialog from "./components/RenameDialog";
import ScrollBar from "components/ScrollBar";
import Dialog from "components/Dialog";
import SimpleRemoveButton from "components/SimpleRemoveButton";
import { downloadSimulationPreview, exportSimulation, setCurrentVariantId, setRenameVariantDialogOpen, } from "store/rs-workspace/actions/rs-workspace.actions";
import { fetchingSimulationPreviewDataSelector, isVariantUsedSelector, renameVariantDialogOpenSelector, simulationPreviewDataSelector, workspaceCurrentSimulationSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getKnowledgeBaseItemState, isEditableSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { setDeleteTarget } from "store/knowledge-base-item/knowledge-base-item.actions";
import { RemovableEntityType } from "store/knowledge-base-item/knowledge-base-item.types";
import { getFieldRestrictions } from "store/knowledge-base-item/knowledge-base-item.utils";
import { getCurrentDateTimeStamp } from "utils/dateTime";
import { isNotEmpty } from "utils/validate";
import { DIALOG_ACTION_TITLE, VariantLabels } from "shared/labels";
import PlusIcon from "icons/plus.svg";
import PencilIcon from "icons/pencil.svg";
import DeleteIcon from "icons/delete.svg";
import DuplicateIcon from "icons/icn-duplicate.svg";
import * as styles from "./styles.module.scss";
var ViewMode;
(function (ViewMode) {
    ViewMode["parameters"] = "parameters";
    ViewMode["preview"] = "preview";
})(ViewMode || (ViewMode = {}));
const viewModeOptions = [
    { value: ViewMode.parameters, label: "Parameters to adjust" },
    { value: ViewMode.preview, label: "Preview" },
];
export const defaultVariantOption = { value: 0, label: "Default" };
const Variants = ({ onVariantChange, triggerPreviewCalculation, restrictions }) => {
    const { id: workspaceId } = useParams();
    const form = useForm();
    const formState = useFormState();
    const dispatch = useDispatch();
    const [viewMode, setViewMode] = useState(ViewMode.parameters);
    const [threeDotsMenuOpen, setThreeDotsMenuOpen] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [nameValidationError, setNameValidationError] = useState("");
    const editable = useSelector(isEditableSelector);
    const { name } = useSelector(getKnowledgeBaseItemState);
    const currentSimulation = useSelector(workspaceCurrentSimulationSelector);
    const fetchingGraphData = useSelector(fetchingSimulationPreviewDataSelector(currentSimulation?.id ?? -1));
    const { lines = [] } = useSelector(simulationPreviewDataSelector);
    const renameDialogOpen = useSelector(renameVariantDialogOpenSelector);
    const currentVariantId = useMemo(() => get(formState?.values, SimulationFormFields._adjustmentId), [formState?.values]);
    const isVariantUsed = useSelector(isVariantUsedSelector(currentVariantId));
    const formVariants = useMemo(() => get(formState?.values, SimulationFormFields.variants) ?? [], [formState?.values]);
    const currentVariantName = useMemo(() => formVariants?.find(({ id }) => id === currentVariantId)?.name ?? "", [formVariants, currentVariantId]);
    const simulationCurrentName = useMemo(() => get(formState?.values, SimulationFormFields.name), [formState?.values]);
    const variantsOptions = useMemo(() => [
        defaultVariantOption,
        ...formVariants.sort((a, b) => a.id - b.id).map(({ id, name }) => ({ value: id, label: name ?? "" })),
    ], [formVariants]);
    const isOriginalVariant = useMemo(() => currentVariantId === 0, [currentVariantId]);
    const onDownload = useCallback(() => {
        const variantName = isOriginalVariant ? "" : `_${currentVariantName}`;
        dispatch(downloadSimulationPreview({
            filename: `${getCurrentDateTimeStamp()}_${name}_${simulationCurrentName}${variantName}`,
        }));
    }, [dispatch, name, simulationCurrentName, currentVariantName, isOriginalVariant]);
    const onExport = useCallback(() => {
        !!currentSimulation &&
            dispatch(exportSimulation({ simulationId: currentSimulation?.id, workspaceId: parseInt(workspaceId) }));
    }, [currentSimulation, dispatch, workspaceId]);
    const getNameValidationError = useCallback((newName) => {
        if (formVariants?.some(({ name }) => name?.toLowerCase() === newName.toLowerCase())) {
            return VariantLabels.VARIANT_NAME_NOT_UNIQUE;
        }
        return "";
    }, [formVariants]);
    const onRename = useCallback((values) => {
        const targetVariant = formVariants?.find(({ id }) => id === currentVariantId);
        if (!targetVariant)
            return;
        const nameChanged = values.name !== targetVariant?.name;
        const updatedVariant = {
            ...targetVariant,
            name: values.name,
        };
        if (nameChanged && isNotEmpty(getNameValidationError(values.name), true)) {
            setNameValidationError(getNameValidationError(values.name));
            return;
        }
        if (nameChanged) {
            form.change(SimulationFormFields.variants, [
                ...formVariants.filter(({ id }) => id !== currentVariantId),
                updatedVariant,
            ]);
        }
        dispatch(setRenameVariantDialogOpen(false));
    }, [currentVariantId, form, formVariants, dispatch, getNameValidationError]);
    const onAddVariant = useCallback(() => {
        const formVariants = get(formState?.values, SimulationFormFields.variants) ?? [];
        const newVariant = {
            id: getNextMaxId(formVariants ?? []),
            name: getNewUniqueName("Variant", formVariants?.map(({ name }) => name ?? "")),
            flowConfig: null,
            recipeParameters: [],
            cellModelParameters: [],
        };
        dispatch(setCurrentVariantId(newVariant.id));
        form.batch(() => {
            form?.mutators.push(SimulationFormFields.variants, newVariant);
            form?.change(SimulationFormFields._adjustmentId, newVariant.id);
        });
        triggerPreviewCalculation({
            [SimulationFormFields.variants]: [...formState.values?.[SimulationFormFields.variants], newVariant],
            [SimulationFormFields._adjustmentId]: newVariant.id,
        });
    }, [dispatch, form, formState?.values, triggerPreviewCalculation]);
    const onDuplicateVariant = useCallback(() => {
        const originalVariant = formVariants?.find(({ id }) => id === currentVariantId);
        if (!originalVariant)
            return;
        const duplicatedVariant = {
            ...originalVariant,
            id: getNextMaxId(formVariants ?? []),
            name: getNewUniqueName("Variant", formVariants?.map(({ name }) => name ?? "")),
        };
        form.change(SimulationFormFields.variants, [...formVariants, duplicatedVariant]);
        dispatch(setCurrentVariantId(duplicatedVariant.id));
    }, [currentVariantId, form, dispatch, formVariants]);
    const onDeleteVariant = useCallback((isConfirmed) => {
        if (!isConfirmed)
            return;
        form.change(SimulationFormFields.variants, [
            ...formVariants.filter(({ id }) => id !== currentVariantId),
        ]);
        dispatch(setCurrentVariantId(0));
        setThreeDotsMenuOpen(false);
    }, [currentVariantId, form, formVariants, dispatch]);
    const openRemoveConfirmationModal = useCallback(() => dispatch(setDeleteTarget({
        id: currentVariantId,
        type: RemovableEntityType.SIMULATION_VARIANT,
    })), [dispatch, currentVariantId]);
    const onVariantConfigurationFieldChange = useCallback((updatedVariantFields) => {
        const unchangedVariants = formVariants?.filter(({ id }) => id !== currentVariantId);
        const currentVariant = formVariants?.find(({ id }) => id === currentVariantId);
        const updatedCurrentVariant = { ...currentVariant, ...updatedVariantFields };
        const previewCalculationPayload = [...unchangedVariants, updatedCurrentVariant];
        triggerPreviewCalculation({ [SimulationFormFields.variants]: previewCalculationPayload });
    }, [formVariants, currentVariantId, triggerPreviewCalculation]);
    const renderVariantConfigurationFields = useCallback((fieldName, fieldIndex) => {
        const index = formVariants?.findIndex(({ id }) => id === currentVariantId);
        return (React.createElement("div", { key: fieldIndex, className: classNames(styles.wrapper, {
                [styles.hidden]: index !== fieldIndex || viewMode === ViewMode.preview,
            }) },
            React.createElement(ScrollBar, { className: styles.scrollBar },
                React.createElement("div", { className: styles.sections },
                    React.createElement(ParameterSection, { type: VariantSourceType.Recipe, name: `${fieldName}.${SimulationFormFields.recipeParameters}`, restrictions: getFieldRestrictions(restrictions, ["RecipeParameters"]), onVariantConfigurationFieldChange: onVariantConfigurationFieldChange }),
                    React.createElement(AlternativeFlowSection, { name: `${fieldName}.${SimulationFormFields.flowConfig}.${SimulationFormFields.stepsConfig}`, restrictions: getFieldRestrictions(restrictions, ["FlowConfig.StepsConfig"]), onVariantConfigurationFieldChange: onVariantConfigurationFieldChange }),
                    React.createElement(ParameterSection, { type: VariantSourceType.CellModel, name: `${fieldName}.${SimulationFormFields.cellModelParameters}`, restrictions: getFieldRestrictions(restrictions, ["CellModelParameters"]), onVariantConfigurationFieldChange: onVariantConfigurationFieldChange })))));
    }, [restrictions, viewMode, currentVariantId, formVariants, onVariantConfigurationFieldChange]);
    const menuItems = useMemo(() => [
        {
            text: "Rename",
            icon: React.createElement(PencilIcon, null),
            onClick: () => dispatch(setRenameVariantDialogOpen(true)),
        },
        {
            text: "Duplicate",
            icon: React.createElement(DuplicateIcon, null),
            onClick: onDuplicateVariant,
        },
        isVariantUsed ? ({
            text: "Delete",
            icon: React.createElement(DeleteIcon, null),
            onClick: () => {
                setRemoveDialogOpen(true);
                openRemoveConfirmationModal();
            },
        }) : (React.createElement(SimpleRemoveButton, { key: "Delete", onOpen: openRemoveConfirmationModal, action: onDeleteVariant })),
    ], [
        onDeleteVariant,
        onDuplicateVariant,
        dispatch,
        setRemoveDialogOpen,
        openRemoveConfirmationModal,
        isVariantUsed,
    ]);
    return (React.createElement("div", { className: styles.root },
        React.createElement("div", { className: styles.top },
            React.createElement(Input, { component: SelectInput, items: variantsOptions, name: SimulationFormFields._adjustmentId, itemRenderer: SelectItemRenderer, onChange: onVariantChange, classes: { component: styles.select }, selectButtonProps: { className: styles["select-button"] }, valueFormatter: ({ value }) => value }),
            editable && !isOriginalVariant && (React.createElement(ThreeDotsMenu, { name: "Actions", classes: { className: styles.menu }, items: menuItems.map((menuItem, index) => {
                    if (isValidElement(menuItem)) {
                        return menuItem;
                    }
                    const item = menuItem;
                    return (React.createElement(DefaultThreeDotsMenuItem, { key: index, closeMenuOnClick: () => setThreeDotsMenuOpen(false), ...item }));
                }), isOpen: threeDotsMenuOpen, setIsOpen: setThreeDotsMenuOpen })),
            editable && (React.createElement(Button, { onClick: onAddVariant, classes: { root: styles.plus }, label: React.createElement(PlusIcon, { width: 15, height: 15 }), showTooltipIfDisabled: true, title: "Add Variant" })),
            renameDialogOpen && (React.createElement(RenameDialog, { name: currentVariantName, onSubmit: onRename, onClose: () => dispatch(setRenameVariantDialogOpen(false)), nameValidationError: nameValidationError, onSetValidationError: setNameValidationError })),
            React.createElement(Dialog, { isOpen: removeDialogOpen, title: VariantLabels.DELETE_TITLE, secondaryButtons: [
                    {
                        onClick: () => {
                            dispatch(setDeleteTarget(null));
                            setRemoveDialogOpen(false);
                        },
                        label: DIALOG_ACTION_TITLE.CANCEL,
                    },
                ], primaryButton: {
                    onClick: () => {
                        onDeleteVariant(true);
                        setRemoveDialogOpen(false);
                    },
                    label: DIALOG_ACTION_TITLE.OK,
                } },
                React.createElement("div", { className: styles.warning }, VariantLabels.DELETE_TEXT))),
        !isOriginalVariant && (React.createElement("div", { className: styles.tabs },
            React.createElement(RadioInputGroup, { onChange: (value) => setViewMode(value), value: viewMode, items: viewModeOptions, isPageSection: true, className: styles.radioInput }))),
        React.createElement(FieldArray, { name: SimulationFormFields.variants }, ({ fields }) => fields.map(renderVariantConfigurationFields)),
        (isOriginalVariant || viewMode === ViewMode.preview) && (React.createElement("div", { className: styles.graph },
            React.createElement(PreviewGraph, null),
            React.createElement(Button, { onClick: onDownload, classes: { root: styles["download-button"] }, label: "Download", role: "button", style: ButtonStyle.Tertiary, size: ButtonSize.Normal, disabled: fetchingGraphData || lines?.length === 0, showTooltipIfDisabled: true }),
            isDev && (React.createElement(Button, { onClick: onExport, classes: { root: styles["export-button"] }, label: "Export", role: "button", style: ButtonStyle.Tertiary, size: ButtonSize.Normal, showTooltipIfDisabled: true }))))));
};
export default Variants;
